import { CSSProperties, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { KaziAtlasLogo } from '../components/KaziAtlasLogo';
import { DeleteAccountPanel } from '../components/DeleteAccountPanel';
import { useAuth } from '../packages/auth-client';
import { DELETION_GRACE_DAYS } from '../packages/api-client/account-deletion-api';
import { useTheme } from '../hooks/useTheme';
import { useMobile } from '../hooks/useWindowWidth';
import { usePageTitle } from '../hooks/usePageTitle';

// Google Play requires a deletion route reachable outside the app, because the
// person asking may have already uninstalled it — and a route that only works
// from inside the app is exactly the case that requirement exists for. This
// page is public: it is submitted as the Play "account deletion URL", and it
// resolves for a signed-out visitor rather than bouncing them to the dashboard.

const INK = 'var(--ink)';
const BODY = 'var(--ink-body)';
const MUTED = 'var(--ink-muted)';
const BORDER = '1px solid var(--border)';

const s: Record<string, CSSProperties> = {
    page: { background: 'var(--bg)', color: BODY, fontFamily: 'var(--font-body)', minHeight: '100vh', display: 'flex', flexDirection: 'column' },
    nav: { borderBottom: BORDER, background: 'var(--surface)' },
    navInner: { maxWidth: 820, margin: '0 auto', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1rem 1.5rem' },
    navHome: { background: 'none', border: 'none', color: MUTED, fontWeight: 600, fontSize: '0.875rem', cursor: 'pointer', padding: 0 },
    main: { maxWidth: 820, margin: '0 auto', width: '100%', padding: '3rem 1.5rem 5rem', flex: 1 },
    h1: { fontSize: 'clamp(2rem, 4vw, 2.75rem)', fontWeight: 900, lineHeight: 1.15, letterSpacing: '-0.02em', color: INK, margin: '0 0 0.5rem' },
    lede: { fontSize: '1.05rem', lineHeight: 1.75, margin: '0 0 2.5rem' },
    h2: { fontSize: '1.35rem', fontWeight: 800, color: INK, margin: '2.5rem 0 0.75rem', letterSpacing: '-0.01em' },
    p: { lineHeight: 1.75, margin: '0 0 1rem' },
    ul: { lineHeight: 1.75, margin: '0 0 1rem', paddingLeft: '1.25rem' },
    link: { color: 'var(--accent-text)', textDecoration: 'none', fontWeight: 600 },
    signIn: { marginTop: '0.5rem' },
    footer: { borderTop: BORDER, background: 'var(--surface)', color: MUTED, fontSize: '0.8125rem', textAlign: 'center', padding: '1.5rem' },
};

function Section({ id, title, children }: { id: string; title: string; children: ReactNode }) {
    return (
        <section id={id}>
            <h2 style={s.h2}>{title}</h2>
            {children}
        </section>
    );
}

export function DeleteAccountPage() {
    usePageTitle('Delete your account');
    const navigate = useNavigate();
    const { theme } = useTheme();
    const isMobile = useMobile();
    const { isAuthenticated, isLoading } = useAuth();

    const goSignIn = () => {
        // LoginPage consumes postLoginRedirect, so signing in lands the visitor
        // back here with the form live rather than on the dashboard.
        sessionStorage.setItem('postLoginRedirect', '/delete-account');
        navigate('/login');
    };

    return (
        <div style={s.page}>
            <nav style={s.nav}>
                <div style={{ ...s.navInner, padding: isMobile ? '1rem' : '1rem 1.5rem' }}>
                    <KaziAtlasLogo size={28} onLight={theme === 'light'} />
                    <button style={s.navHome} onClick={() => navigate('/')}>← Back to home</button>
                </div>
            </nav>

            <main style={s.main}>
                <h1 style={s.h1}>Delete your account</h1>

                <p style={s.lede}>
                    You can delete your KaziAtlas account yourself, from this page or from
                    Profile in the web, iOS, or Android app. You do not need to contact
                    support, and you do not need the app installed.
                </p>

                <Section id="what" title="What happens">
                    <ul style={s.ul}>
                        <li>Your profile, personal settings, and API keys are deleted.</li>
                        <li>
                            Deletion completes {DELETION_GRACE_DAYS} days after you confirm.
                            Until then you can sign in as usual and cancel it — after that it
                            cannot be undone.
                        </li>
                        <li>
                            Work you created inside a workspace belongs to that workspace and
                            stays with it. If you need that removed too, ask the workspace
                            owner, or write to us using the{' '}
                            <a href="/support" style={s.link}>support page</a>.
                        </li>
                        <li>
                            If you own a workspace that other people are still in, transfer
                            ownership or close the workspace first — otherwise deleting your
                            account would leave them without an owner.
                        </li>
                    </ul>
                </Section>

                <Section id="delete" title="Delete your account now">
                    {isLoading ? (
                        <p style={s.p}>Checking your sign-in…</p>
                    ) : isAuthenticated ? (
                        <DeleteAccountPanel />
                    ) : (
                        <>
                            <p style={s.p}>
                                Sign in first so we know which account to delete. We ask you to
                                sign in rather than emailing a link, so nobody with access to
                                your inbox can delete your account on your behalf.
                            </p>
                            <div style={s.signIn}>
                                <button
                                    type="button"
                                    className="btn btn-primary btn-md"
                                    onClick={goSignIn}
                                >
                                    Sign in to continue
                                </button>
                            </div>
                        </>
                    )}
                </Section>

                <Section id="changed-mind" title="If you change your mind">
                    <p style={s.p}>
                        Sign in again before the deletion date. You will see a banner at the
                        top of the app saying when the account is scheduled to be deleted,
                        with a button to keep it. For the same reason we ask you to sign in
                        above, there is no cancellation link sent by email.
                    </p>
                </Section>
            </main>

            <footer style={s.footer}>
                <a href="/privacy" style={s.link}>Privacy Policy</a>
                {' · '}
                <a href="/support" style={s.link}>Support</a>
            </footer>
        </div>
    );
}
